import { LegendList } from "@legendapp/list/react";
import type {
  EnvironmentId,
  LiveSubagent,
  LiveSubagentId,
  LiveSubagentTimelineItem,
  ServerProviderSkill,
  ThreadId,
} from "@t3tools/contracts";
import { scopeThreadRef } from "@t3tools/client-runtime/environment";
import { Bot, CircleAlert, LoaderCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "~/lib/utils";
import { i18n } from "~/i18n";
import { serverEnvironment } from "~/state/server";
import { useEnvironmentQuery } from "~/state/query";
import { LiveSubagentTimelineItem as LiveSubagentTimelineItemContent } from "./LiveSubagentTimelineItem";
import {
  liveSubagentStatusDotClass,
  liveSubagentStatusLabel,
  liveSubagentDisplayName,
  liveSubagentCompactRuntimeMetadata,
  liveSubagentReasoningEffortLabel,
  liveSubagentSummaryText,
  liveSubagentUnavailableMessage,
} from "./liveSubagentPresentation";

export interface LiveSubagentSelectionRequest {
  readonly agentId: LiveSubagentId;
  readonly requestId: number;
}

export function resolveLiveSubagentSelection(input: {
  readonly agents: ReadonlyArray<LiveSubagent>;
  readonly currentAgentId: LiveSubagentId | null;
  readonly request: LiveSubagentSelectionRequest | null;
  readonly appliedRequestId: number | null;
}): {
  readonly agentId: LiveSubagentId | null;
  readonly appliedRequestId: number | null;
} {
  if (input.agents.length === 0) {
    return { agentId: null, appliedRequestId: input.appliedRequestId };
  }
  if (input.request && input.request.requestId !== input.appliedRequestId) {
    const requestedAgent = input.agents.find((agent) => agent.id === input.request?.agentId);
    return {
      agentId: requestedAgent?.id ?? input.agents[0]!.id,
      appliedRequestId: input.request.requestId,
    };
  }
  if (input.currentAgentId && input.agents.some((agent) => agent.id === input.currentAgentId)) {
    return { agentId: input.currentAgentId, appliedRequestId: input.appliedRequestId };
  }
  return { agentId: input.agents[0]!.id, appliedRequestId: input.appliedRequestId };
}

export function LiveSubagentTruncationNotice() {
  return (
    <div
      role="status"
      className="mx-3 mb-2 rounded-lg border border-border/70 bg-muted/30 px-3 py-2 text-xs leading-relaxed text-muted-foreground"
    >
      {i18n.t("liveSubagents.truncationNotice")}
    </div>
  );
}

export function liveSubagentTimelineListKey(agentId: LiveSubagentId | null): string {
  return JSON.stringify(["live-subagent", agentId]);
}

export function LiveSubagentPanel(props: {
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly markdownCwd: string | undefined;
  readonly selectionRequest?: LiveSubagentSelectionRequest | null;
  readonly skills: ReadonlyArray<Pick<ServerProviderSkill, "name" | "displayName">>;
  readonly workspaceRoot: string | undefined;
}) {
  const rosterAtom = useMemo(
    () =>
      serverEnvironment.liveSubagentRoster({
        environmentId: props.environmentId,
        input: { threadId: props.threadId },
      }),
    [props.environmentId, props.threadId],
  );
  const roster = useEnvironmentQuery(rosterAtom);
  const [selectedAgentId, setSelectedAgentId] = useState<LiveSubagentId | null>(null);
  const appliedSelectionRequestId = useRef<number | null>(null);
  const agents = roster.data?.agents ?? [];
  const selectedAgent = agents.find((agent) => agent.id === selectedAgentId) ?? null;
  const timelineAgentId = selectedAgent?.id ?? null;
  const hasActivityFacet =
    roster.data?.facets?.lifecycleActivity === true || roster.data?.facets?.transcript === true;
  const observationKey = roster.data?.observationKey ?? null;
  const selectedGeneration = useRef<string | null>(observationKey);

  useEffect(() => {
    if (selectedGeneration.current !== observationKey) {
      selectedGeneration.current = observationKey;
      appliedSelectionRequestId.current = null;
      if (selectedAgentId !== null) setSelectedAgentId(null);
      return;
    }
    const next = resolveLiveSubagentSelection({
      agents,
      currentAgentId: selectedAgentId,
      request: props.selectionRequest ?? null,
      appliedRequestId: appliedSelectionRequestId.current,
    });
    appliedSelectionRequestId.current = next.appliedRequestId;
    if (next.agentId !== selectedAgentId) {
      setSelectedAgentId(next.agentId);
    }
  }, [agents, observationKey, props.selectionRequest, selectedAgentId]);

  const timelineAtom = useMemo(
    () =>
      timelineAgentId && hasActivityFacet && observationKey
        ? serverEnvironment.liveSubagentTimeline({
            environmentId: props.environmentId,
            input: {
              threadId: props.threadId,
              agentId: timelineAgentId,
              observationKey,
            },
          })
        : null,
    [hasActivityFacet, observationKey, props.environmentId, props.threadId, timelineAgentId],
  );
  const timeline = useEnvironmentQuery(timelineAtom);
  const threadRef = useMemo(
    () => scopeThreadRef(props.environmentId, props.threadId),
    [props.environmentId, props.threadId],
  );
  const renderTimelineItem = useCallback(
    ({ item }: { item: LiveSubagentTimelineItem }) => (
      <LiveSubagentTimelineItemContent
        item={item}
        markdownCwd={props.markdownCwd}
        threadRef={threadRef}
        skills={props.skills}
        workspaceRoot={props.workspaceRoot}
      />
    ),
    [props.markdownCwd, props.skills, props.workspaceRoot, threadRef],
  );

  if (roster.isPending && roster.data === null) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center gap-2 text-sm text-muted-foreground">
        <LoaderCircle className="size-4" />
        {i18n.t("liveSubagents.connecting")}
      </div>
    );
  }
  if (roster.error) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
        {roster.error}
      </div>
    );
  }

  const rosterUnavailable =
    roster.data?.status === "unavailable" || roster.data?.status === "expired";
  if (rosterUnavailable && agents.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center p-6">
        <div className="max-w-xs text-center">
          <CircleAlert className="mx-auto size-5 text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">
            {i18n.t(
              roster.data?.status === "expired"
                ? "liveSubagents.runtimeEnded"
                : "liveSubagents.agentsUnavailable",
            )}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {roster.data?.status === "expired"
              ? i18n.t("liveSubagents.runtimeEndedDescription")
              : liveSubagentUnavailableMessage(roster.data?.reason)}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      <aside className="flex w-44 shrink-0 flex-col border-r border-border/70">
        <div className="border-b border-border/70 px-3 py-2">
          <div className="text-xs font-medium">{i18n.t("liveSubagents.liveSubagents")}</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {i18n.t(
              agents.length === 1 ? "liveSubagents.agentCountOne" : "liveSubagents.agentCountOther",
              { count: agents.length },
            )}
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
          {agents.length === 0 ? (
            <div className="px-2 py-5 text-center text-xs text-muted-foreground">
              {i18n.t("liveSubagents.empty")}
            </div>
          ) : (
            agents.map((agent) => (
              <button
                key={agent.id}
                type="button"
                onClick={() => setSelectedAgentId(agent.id)}
                className={cn(
                  "mb-1 w-full rounded-md px-2.5 py-2 text-left",
                  selectedAgentId === agent.id
                    ? "bg-accent text-foreground"
                    : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
                )}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "size-1.5 shrink-0 rounded-full",
                      liveSubagentStatusDotClass(agent.phase),
                    )}
                  />
                  <span className="min-w-0 flex-1 truncate text-xs font-medium">
                    {liveSubagentDisplayName(agent)}
                  </span>
                </div>
                <div className="mt-1 truncate pl-3.5 text-[10px] text-muted-foreground">
                  {liveSubagentStatusLabel(agent.phase)}
                </div>
                {liveSubagentCompactRuntimeMetadata(agent) ? (
                  <div
                    className="mt-0.5 truncate pl-3.5 text-[10px] text-muted-foreground"
                    title={liveSubagentCompactRuntimeMetadata(agent) ?? undefined}
                  >
                    {liveSubagentCompactRuntimeMetadata(agent)}
                  </div>
                ) : null}
              </button>
            ))
          )}
        </div>
      </aside>

      <section className="flex min-w-0 flex-1 flex-col">
        <header className="border-b border-border/70 px-3 py-2">
          <div className="flex items-center gap-2">
            <Bot className="size-4 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate text-xs font-medium">
              {selectedAgent
                ? liveSubagentDisplayName(selectedAgent)
                : i18n.t("liveSubagents.agentFallback")}
            </span>
            {selectedAgent ? (
              <span className="text-[10px] text-muted-foreground">
                {liveSubagentStatusLabel(selectedAgent.phase)}
              </span>
            ) : null}
          </div>
          {selectedAgent && liveSubagentSummaryText(selectedAgent) ? (
            <p className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-muted-foreground">
              {liveSubagentSummaryText(selectedAgent)}
            </p>
          ) : null}
          {selectedAgent && (selectedAgent.model || selectedAgent.reasoningEffort) ? (
            <div className="mt-1.5 flex min-w-0 flex-wrap gap-1.5 text-[10px] text-muted-foreground">
              {selectedAgent.model ? (
                <span
                  className="max-w-full truncate rounded bg-muted px-1.5 py-0.5"
                  title={selectedAgent.model}
                >
                  {i18n.t("liveSubagents.model", { model: selectedAgent.model })}
                </span>
              ) : null}
              {selectedAgent.reasoningEffort ? (
                <span className="rounded bg-muted px-1.5 py-0.5">
                  {i18n.t("liveSubagents.reasoningEffort", {
                    effort: liveSubagentReasoningEffortLabel(selectedAgent.reasoningEffort),
                  })}
                </span>
              ) : null}
            </div>
          ) : null}
        </header>
        <div className="relative min-h-0 flex-1">
          {!hasActivityFacet ? (
            <div className="flex h-full items-center justify-center p-5 text-center text-xs text-muted-foreground">
              {i18n.t("liveSubagents.activityUnavailable")}
            </div>
          ) : timeline.isPending && timeline.data === null ? (
            <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
              {i18n.t("liveSubagents.loadingActivity")}
            </div>
          ) : timeline.error ? (
            <div className="flex h-full items-center justify-center p-5 text-center text-xs text-muted-foreground">
              {timeline.error}
            </div>
          ) : timeline.data?.status === "unavailable" ? (
            <div className="flex h-full items-center justify-center p-5 text-center text-xs text-muted-foreground">
              {liveSubagentUnavailableMessage(timeline.data.reason)}
            </div>
          ) : timeline.data?.status === "expired" ? (
            <div className="flex h-full items-center justify-center p-5 text-center text-xs text-muted-foreground">
              {i18n.t("liveSubagents.runtimeEndedDescription")}
            </div>
          ) : (timeline.data?.items.length ?? 0) === 0 ? (
            <div className="flex h-full items-center justify-center p-5 text-center text-xs text-muted-foreground">
              {roster.data?.facets?.transcript === false
                ? i18n.t("liveSubagents.lifecycleOnly")
                : i18n.t("liveSubagents.emptyActivity")}
            </div>
          ) : (
            <LegendList<LiveSubagentTimelineItem>
              key={liveSubagentTimelineListKey(selectedAgentId)}
              data={timeline.data?.items ?? []}
              keyExtractor={(item) => item.id}
              getItemType={(item) => item.kind}
              renderItem={renderTimelineItem}
              estimatedItemSize={88}
              drawDistance={500}
              recycleItems={false}
              initialScrollAtEnd
              maintainScrollAtEnd={{
                animated: false,
                on: { dataChange: true, itemLayout: true, layout: true },
              }}
              className="h-full overflow-x-hidden overscroll-y-contain py-2"
              ListHeaderComponent={
                timeline.data?.truncated ? <LiveSubagentTruncationNotice /> : null
              }
            />
          )}
        </div>
      </section>
    </div>
  );
}
